import React, { useContext } from "react";
import { LayoutContext, constants } from "./LayoutContext";
import "./Pagination.css";

// Utility to join base URL and path with exactly one slash
function joinUrl(base, path) {
  if (!base) return path;
  if (!path) return base;
  return base.replace(/\/+$/, "") + "/" + path.replace(/^\/+/, "");
}

// Utility to normalize paths for comparison
function normalizePath(path) {
  if (!path) return "/";
  let p = path.trim();
  if (!p.startsWith("/")) p = "/" + p;
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

export const Pagination = props => {
  const layoutContext = useContext(LayoutContext);
  const { theme } = layoutContext;
  const { pagePath, i18n, pagination } = props;

  // Helper to get language prefix for URLs
  const langPrefix =
    i18n.i18n_prefix === i18n.i18n_main ? "" : `/${i18n.i18n_prefix}`;

  // Home URL
  const homeUrl = langPrefix || "/";

  // Category URL: parent of current page
  const parentUrl = (() => {
    let path = pagePath;
    if (langPrefix && path.startsWith(langPrefix)) {
      path = path.slice(langPrefix.length);
      if (!path.startsWith("/")) path = "/" + path;
    }
    const segments = path.split("/").filter(Boolean);
    if (segments.length > 1) {
      segments.pop();
      const joined = "/" + segments.join("/");
      return langPrefix ? langPrefix + joined : joined;
    }
    return homeUrl;
  })();

  // Breadcrumb labels
  const homeLabel = pagination.pagination_main;
  const categoryLabel = pagination.pagination_category;
  const mainItemLabel = pagination.main_item_translated_;

  // Use GATSBY_SITE_URL from env, ensure no trailing slash
  const siteUrl = (process.env.GATSBY_SITE_URL || "").replace(/\/$/, "");

  // Normalize paths for comparison
  const normalizedPagePath = normalizePath(pagePath);
  const normalizedParentUrl = normalizePath(parentUrl);
  const normalizedHomeUrl = normalizePath(homeUrl);

  // Build breadcrumb array as objects for easier handling
  const crumbs = [];
  if (homeLabel) {
    crumbs.push({
      type: "link",
      key: "home",
      href: joinUrl(siteUrl, homeUrl),
      label: homeLabel,
    });
  }
  if (categoryLabel) {
    if (
      normalizedParentUrl !== normalizedHomeUrl &&
      normalizedPagePath !== normalizedParentUrl
    ) {
      crumbs.push({
        type: "link",
        key: "category",
        href: joinUrl(siteUrl, parentUrl),
        label: categoryLabel,
      });
    } else {
      crumbs.push({
        type: "text",
        key: "category",
        label: categoryLabel,
      });
    }
  }
  if (mainItemLabel) {
    crumbs.push({
      type: "text",
      key: "mainItem",
      label: mainItemLabel,
    });
  }

  // Hide if only homeLabel is present
  const isOnlyHome = crumbs.length === 1;

  return (
    <nav
      className={
        "pagination" +
        (theme === constants.THEME_LIGHT ? " theme-light" : " theme-dark") +
        (isOnlyHome ? " pagination-hidden" : "")
      }
      id="pagination"
      aria-label="Breadcrumb"
      style={
        isOnlyHome
          ? { height: 0, overflow: "hidden", padding: 0, border: 0 }
          : {}
      }
    >
      {!isOnlyHome && (
        <ol>
          {crumbs.map((crumb, idx) => {
            const isLast = idx === crumbs.length - 1;
            return (
              <li
                key={crumb.key}
                style={{ display: "flex", alignItems: "center" }}
              >
                {idx > 0 && <span className="separator">›</span>}
                {crumb.type === "link" && !isLast ? (
                  <a href={crumb.href}>{crumb.label}</a>
                ) : (
                  <span aria-current={isLast ? "page" : undefined}>
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
};
